import { useState, useEffect } from 'react';
import { getCatalog, getAvailableDates, getSaleVisibility, getCustomerAnnouncements } from '../../lib/customerApi';
import { useCart } from '../../contexts/CartContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { ShoppingCart, Tag, Package, ChevronDown, ChevronUp, Info, Plus, Minus, Search, X } from 'lucide-react';

const INITIAL_DISPLAY = 9;
const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const PLACEHOLDER_IMAGE = 'https://static.prod-images.emergentagent.com/jobs/a6ade5f6-3444-4c35-926e-b151ef73f87c/images/2ded88222df5c0bc13af15a2d9282c5dd6dd7a7aed6be2ca8410dd3d1a4a8229.png';
const DESCRIPTION_PREVIEW_LENGTH = 20; // 説明文のプレビュー文字数

// 画像URLをフルURLに変換（iPhone Safari対応）
const getFullImageUrl = (url) => {
  if (!url) return null;
  // 既にフルURLの場合はそのまま返す
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  // 相対パスの場合はAPI_URLを付加
  return `${API_URL}${url}`;
};

// 価格表示（0の場合は「時価」と表示）
const formatPrice = (price) => {
  if (price === 0 || price === null || price === undefined) {
    return '時価';
  }
  return `¥${price.toLocaleString()}`;
};

export const CustomerCatalog = () => {
  const [salesItems, setSalesItems] = useState([]);
  const [productItems, setProductItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [favoriteProductIds, setFavoriteProductIds] = useState([]);
  const [favoriteSaleIds, setFavoriteSaleIds] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showAllSales, setShowAllSales] = useState(false);
  const [showAllProducts, setShowAllProducts] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [quantities, setQuantities] = useState({}); // 各商品の数量を管理
  const [isSalePreparation, setIsSalePreparation] = useState(true); // 特売準備中フラグ（デフォルトは準備中）
  const [searchQuery, setSearchQuery] = useState(''); // 検索クエリ
  const [announcements, setAnnouncements] = useState([]);
  const { addItem } = useCart();

  // 特売公開状態をAPIから取得
  const fetchSaleVisibility = async () => {
    try {
      const res = await getSaleVisibility();
      // is_published=trueなら公開中（準備中ではない）
      setIsSalePreparation(!res.data.is_published);
    } catch (error) {
      console.error('Failed to fetch sale visibility:', error);
      // エラー時は準備中として扱う
      setIsSalePreparation(true);
    }
  };

  useEffect(() => {
    // 初回取得
    fetchSaleVisibility();
    
    // 1分ごとに公開状態をチェック
    const interval = setInterval(() => {
      fetchSaleVisibility();
    }, 60000);
    
    return () => clearInterval(interval);
  }, []);

  useEffect(() => { fetchCatalog(); fetchAnnouncements(); }, []);

  const fetchAnnouncements = async () => {
    try {
      const res = await getCustomerAnnouncements();
      setAnnouncements(res.data || []);
    } catch (error) {}
  };

  const fetchCatalog = async () => {
    try {
      const res = await getCatalog();
      setSalesItems(res.data.sales || []);
      setProductItems(res.data.products || []);
      setCategories(res.data.categories || []);
      setFavoriteProductIds(res.data.favorite_product_ids || []);
      setFavoriteSaleIds(res.data.favorite_sale_ids || []);
    } catch (error) {
      toast.error('カタログの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  // 検索フィルタ関数
  const matchesSearch = (item) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      item.name?.toLowerCase().includes(query) ||
      item.origin?.toLowerCase().includes(query) ||
      item.spec?.toLowerCase().includes(query) ||
      item.description?.toLowerCase().includes(query)
    );
  };

  // カテゴリフィルタ + 検索フィルタ
  const filteredSalesItems = (selectedCategory === 'all' || selectedCategory === 'sales'
    ? salesItems 
    : selectedCategory === 'favorites'
    ? salesItems.filter(item => favoriteSaleIds.includes(item.id))
    : salesItems.filter(item => item.category_id === selectedCategory)
  ).filter(matchesSearch);
  
  const filteredProductItems = (selectedCategory === 'all'
    ? productItems
    : selectedCategory === 'sales'
    ? []
    : selectedCategory === 'favorites'
    ? productItems.filter(item => favoriteProductIds.includes(item.id))
    : productItems.filter(item => item.category_id === selectedCategory)
  ).filter(matchesSearch);

  const handleAddToCart = (item) => {
    const qty = quantities[`${item.item_type}-${item.id}`] || 1;
    // 指定数量分追加
    for (let i = 0; i < qty; i++) {
      addItem(item);
    }
    toast.success(`${item.name} x${qty} をカートに追加しました`);
    // 数量をリセット
    setQuantities(prev => ({ ...prev, [`${item.item_type}-${item.id}`]: 1 }));
  };

  const getQuantity = (item) => {
    return quantities[`${item.item_type}-${item.id}`] || 1;
  };

  const updateQuantity = (item, delta) => {
    const key = `${item.item_type}-${item.id}`;
    const current = quantities[key] || 1;
    const newQty = Math.max(1, current + delta);
    setQuantities(prev => ({ ...prev, [key]: newQty }));
  };

  const handleShowDetail = (item) => {
    setSelectedItem(item);
    setDetailDialogOpen(true);
  };

  const displayedSales = showAllSales ? filteredSalesItems : filteredSalesItems.slice(0, INITIAL_DISPLAY);
  const displayedProducts = showAllProducts ? filteredProductItems : filteredProductItems.slice(0, INITIAL_DISPLAY);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;
  }

  const ProductCard = ({ item, isSale = false }) => {
    const [imgError, setImgError] = useState(false);
    const imageUrl = getFullImageUrl(item.image_url);
    const hasDescription = item.description && item.description.trim().length > 0;
    const descriptionPreview = hasDescription 
      ? item.description.slice(0, DESCRIPTION_PREVIEW_LENGTH) 
      : '';
    const hasMoreDescription = hasDescription && item.description.length > DESCRIPTION_PREVIEW_LENGTH;
    
    return (
      <Card className={`overflow-hidden border-slate-200 hover:shadow-lg transition-shadow flex flex-col h-full ${isSale ? 'ring-2 ring-red-400' : ''}`}>
        <div className="relative aspect-square bg-slate-100 flex-shrink-0">
          {imageUrl && !imgError ? (
            <img 
              src={imageUrl} 
              alt={item.name} 
              className="w-full h-full object-cover" 
              loading="lazy"
              onError={() => setImgError(true)}
            />
          ) : (
            <div className="relative w-full h-full">
              <img 
                src={PLACEHOLDER_IMAGE} 
                alt="画像準備中" 
                className="w-full h-full object-cover opacity-70" 
                loading="lazy"
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="bg-white/80 text-slate-600 text-xs md:text-sm px-2 py-1 rounded-full font-medium">
                  画像準備中
                </span>
              </div>
            </div>
          )}
          {isSale && (
            <Badge className="absolute top-1 left-1 md:top-2 md:left-2 bg-red-500 text-white text-xs px-1 md:px-2">
              特売
            </Badge>
          )}
        </div>
      <CardContent className="p-2 md:p-3 flex flex-col flex-1">
        {/* 上部コンテンツ */}
        <div>
          <h3 className="font-semibold text-slate-900 text-xs md:text-sm line-clamp-2 min-h-[2.5em]">{item.name}</h3>
          <p className="text-[10px] md:text-xs text-slate-500 mt-0.5 md:mt-1 line-clamp-1 min-h-[1.2em]">
            {(item.origin || item.spec) ? (
              <>
                {item.origin && <span>{item.origin}</span>}
                {item.origin && item.spec && <span> / </span>}
                {item.spec && <span>{item.spec}</span>}
              </>
            ) : (
              <span>&nbsp;</span>
            )}
          </p>
          <div className="mt-1 md:mt-2 flex items-baseline gap-0.5 md:gap-1">
            <span className={`text-sm md:text-lg font-bold ${isSale ? 'text-red-600' : 'text-slate-900'}`}>
              {formatPrice(item.price)}
            </span>
            {item.price !== 0 && <span className="text-[10px] md:text-xs text-slate-500">/{item.unit}</span>}
          </div>
          {/* 説明プレビュー - 常に同じ高さを確保 */}
          <div className="h-6 md:h-7 mt-1">
            {hasDescription ? (
              <div 
                className="flex items-center text-[10px] md:text-xs text-slate-600 cursor-pointer hover:text-slate-800"
                onClick={() => handleShowDetail(item)}
              >
                <span className="truncate">{descriptionPreview}</span>
                {hasMoreDescription && (
                  <span className="text-blue-500 ml-0.5 flex-shrink-0">➡</span>
                )}
              </div>
            ) : null}
          </div>
        </div>
        {/* 下部コンテンツ（常に下部に固定） */}
        <div className="mt-auto pt-1">
          {/* 数量選択UI */}
          <div className="flex items-center justify-center gap-1 md:gap-2">
            <Button
              variant="outline"
              size="icon"
              className="h-6 w-6 md:h-8 md:w-8"
              onClick={() => updateQuantity(item, -1)}
            >
              <Minus className="h-3 w-3 md:h-4 md:w-4" />
            </Button>
            <span className="w-6 md:w-8 text-center font-bold text-sm md:text-base">
              {getQuantity(item)}
            </span>
            <Button
              variant="outline"
              size="icon"
              className="h-6 w-6 md:h-8 md:w-8"
              onClick={() => updateQuantity(item, 1)}
            >
              <Plus className="h-3 w-3 md:h-4 md:w-4" />
            </Button>
          </div>
          <Button
            className={`w-full mt-1 md:mt-2 h-7 md:h-9 text-xs md:text-sm ${isSale ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}
            size="sm"
            onClick={() => handleAddToCart(item)}
          >
            <ShoppingCart className="h-3 w-3 md:h-4 md:w-4 mr-0.5 md:mr-1" />
            <span className="hidden md:inline">カートに追加</span>
            <span className="md:hidden">追加</span>
          </Button>
        </div>
      </CardContent>
    </Card>
    );
  };

  return (
    <div className="space-y-4 md:space-y-6" data-testid="customer-catalog">
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-slate-900">商品カタログ</h1>
        <p className="text-slate-500 mt-1 text-sm">ご注文の商品をお選びください</p>
      </div>

      {/* お知らせ */}
      {announcements.length > 0 && (
        <div className="space-y-2" data-testid="announcements">
          {announcements.map(a => (
            <div key={a.id} className="bg-amber-50 border border-amber-300 rounded-lg px-4 py-3 text-sm text-amber-900 whitespace-pre-wrap">
              {a.message}
            </div>
          ))}
        </div>
      )}

      {/* 検索バー */}
      <div className="relative" data-testid="search-bar">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          type="text"
          placeholder="商品名・産地・規格で検索..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 pr-10 h-11 text-base border-slate-300 focus:border-blue-500 focus:ring-blue-500"
          data-testid="search-input"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
            data-testid="clear-search"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* 検索結果表示 */}
      {searchQuery && (
        <div className="text-sm text-slate-600">
          「{searchQuery}」の検索結果: {filteredSalesItems.length + filteredProductItems.length}件
        </div>
      )}

      {/* カテゴリフィルタ */}
      <div className="flex flex-wrap gap-2" data-testid="category-filter">
        <Button
          variant={selectedCategory === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setSelectedCategory('all')}
          className={selectedCategory === 'all' ? 'bg-blue-600 hover:bg-blue-700' : ''}
        >
          すべて
        </Button>
        {(favoriteProductIds.length > 0 || favoriteSaleIds.length > 0) && (
          <Button
            variant={selectedCategory === 'favorites' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSelectedCategory('favorites')}
            className={selectedCategory === 'favorites' ? 'bg-amber-500 hover:bg-amber-600' : 'border-amber-400 text-amber-600 hover:bg-amber-50'}
          >
            ⭐ AI提案
          </Button>
        )}
        <Button
          variant={selectedCategory === 'sales' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setSelectedCategory('sales')}
          className={selectedCategory === 'sales' ? 'bg-red-600 hover:bg-red-700' : 'border-red-300 text-red-600 hover:bg-red-50'}
        >
          特売
        </Button>
        {categories.map((cat) => (
          <Button
            key={cat.id}
            variant={selectedCategory === cat.id ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSelectedCategory(cat.id)}
            className={selectedCategory === cat.id ? 'bg-blue-600 hover:bg-blue-700' : ''}
          >
            {cat.name}
          </Button>
        ))}
      </div>

      {/* 特売商品セクション */}
      {selectedCategory !== 'sales' && (
        <section>
          <div className="flex items-center gap-2 mb-4">
            <Tag className="h-5 w-5 text-red-600" />
            <h2 className="text-xl font-bold text-red-600">特売商品</h2>
            {!isSalePreparation && <Badge className="bg-red-100 text-red-800">{filteredSalesItems.length}点</Badge>}
          </div>
          
          {isSalePreparation ? (
            <Card className="border-red-200 bg-red-50">
              <CardContent className="py-8 text-center">
                <Tag className="h-12 w-12 text-red-300 mx-auto mb-3" />
                <p className="text-red-600 font-semibold text-lg">特売準備中</p>
                <p className="text-red-500 text-sm mt-1">しばらくお待ちください</p>
              </CardContent>
            </Card>
          ) : filteredSalesItems.length > 0 ? (
            <>
              <div className="grid grid-cols-3 gap-2 md:gap-4 auto-rows-fr">
                {displayedSales.map((item) => (
                  <ProductCard key={item.id} item={item} isSale={true} />
                ))}
              </div>

              {filteredSalesItems.length > INITIAL_DISPLAY && (
                <div className="text-center mt-4">
                  <Button
                    variant="outline"
                    onClick={() => setShowAllSales(!showAllSales)}
                    className="border-red-300 text-red-600 hover:bg-red-50"
                  >
                    {showAllSales ? (
                      <>閉じる <ChevronUp className="h-4 w-4 ml-1" /></>
                    ) : (
                      <>もっと見る ({filteredSalesItems.length - INITIAL_DISPLAY}点) <ChevronDown className="h-4 w-4 ml-1" /></>
                    )}
                  </Button>
                </div>
              )}
            </>
          ) : (
            <Card className="border-slate-200">
              <CardContent className="py-6 text-center text-slate-500">
                <p>現在、特売商品はありません</p>
              </CardContent>
            </Card>
          )}
        </section>
      )}

      {/* 特売フィルタ選択時 */}
      {selectedCategory === 'sales' && (
        <section>
          <div className="flex items-center gap-2 mb-4">
            <Tag className="h-5 w-5 text-red-600" />
            <h2 className="text-xl font-bold text-red-600">特売商品</h2>
            {!isSalePreparation && <Badge className="bg-red-100 text-red-800">{salesItems.length}点</Badge>}
          </div>
          
          {isSalePreparation ? (
            <Card className="border-red-200 bg-red-50">
              <CardContent className="py-12 text-center">
                <Tag className="h-16 w-16 text-red-300 mx-auto mb-4" />
                <p className="text-red-600 font-semibold text-xl">特売準備中</p>
                <p className="text-red-500 text-sm mt-2">しばらくお待ちください</p>
              </CardContent>
            </Card>
          ) : salesItems.length > 0 ? (
            <div className="grid grid-cols-3 gap-2 md:gap-4 auto-rows-fr">
              {salesItems.map((item) => (
                <ProductCard key={item.id} item={item} isSale={true} />
              ))}
            </div>
          ) : (
            <Card className="border-slate-200">
              <CardContent className="py-8 text-center text-slate-500">
                <p>現在、特売商品はありません</p>
              </CardContent>
            </Card>
          )}
        </section>
      )}

      {/* AI提案フィルタ選択時 */}
      {selectedCategory === 'favorites' && (
        <section>
          <div className="flex items-center gap-2 mb-4">
            <span className="text-xl">⭐</span>
            <h2 className="text-xl font-bold text-amber-600">AI提案商品</h2>
            <Badge className="bg-amber-100 text-amber-800">{filteredSalesItems.length + filteredProductItems.length}点</Badge>
          </div>
          
          {(filteredSalesItems.length > 0 || filteredProductItems.length > 0) ? (
            <div className="grid grid-cols-3 gap-2 md:gap-4 auto-rows-fr">
              {/* AI提案の特売商品 */}
              {filteredSalesItems.map((item) => (
                <ProductCard key={`sale-${item.id}`} item={item} isSale={true} />
              ))}
              {/* AI提案のレギュラー商品 */}
              {filteredProductItems.map((item) => (
                <ProductCard key={`product-${item.id}`} item={item} isSale={false} />
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <p>AI提案商品が設定されていません</p>
            </div>
          )}
        </section>
      )}

      {/* レギュラー商品セクション */}
      {filteredProductItems.length > 0 && selectedCategory !== 'sales' && selectedCategory !== 'favorites' && (
        <section>
          <div className="flex items-center gap-2 mb-4">
            <Package className="h-5 w-5 text-slate-600" />
            <h2 className="text-xl font-bold text-slate-900">レギュラー商品</h2>
            <Badge className="bg-slate-100 text-slate-800">{filteredProductItems.length}点</Badge>
          </div>
          
          <div className="grid grid-cols-3 gap-2 md:gap-4 auto-rows-fr">
            {displayedProducts.map((item) => (
              <ProductCard key={item.id} item={item} isSale={false} />
            ))}
          </div>

          {filteredProductItems.length > INITIAL_DISPLAY && (
            <div className="text-center mt-4">
              <Button
                variant="outline"
                onClick={() => setShowAllProducts(!showAllProducts)}
              >
                {showAllProducts ? (
                  <>閉じる <ChevronUp className="h-4 w-4 ml-1" /></>
                ) : (
                  <>もっと見る ({filteredProductItems.length - INITIAL_DISPLAY}点) <ChevronDown className="h-4 w-4 ml-1" /></>
                )}
              </Button>
            </div>
          )}
        </section>
      )}

      {filteredSalesItems.length === 0 && filteredProductItems.length === 0 && (
        <div className="text-center py-16 text-slate-500">
          <Package className="h-16 w-16 text-slate-300 mx-auto mb-4" />
          <p>{selectedCategory === 'all' ? '商品がありません' : 'このカテゴリには商品がありません'}</p>
        </div>
      )}

      {/* 商品詳細ダイアログ */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedItem?.is_sale && <Badge className="bg-red-500 text-white">特売</Badge>}
              {selectedItem?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedItem?.image_url && (
              <div className="aspect-video bg-slate-100 rounded-lg overflow-hidden">
                <img src={selectedItem.image_url} alt={selectedItem.name} className="w-full h-full object-cover" />
              </div>
            )}
            <div className="flex items-baseline gap-2">
              <span className={`text-2xl font-bold ${selectedItem?.is_sale ? 'text-red-600' : 'text-slate-900'}`}>
                {formatPrice(selectedItem?.price)}
              </span>
              {selectedItem?.price !== 0 && <span className="text-slate-500">/{selectedItem?.unit}</span>}
            </div>
            {(selectedItem?.origin || selectedItem?.spec) && (
              <p className="text-sm text-slate-500">
                {selectedItem?.origin && <span>産地: {selectedItem.origin}</span>}
                {selectedItem?.origin && selectedItem?.spec && <span> / </span>}
                {selectedItem?.spec && <span>規格: {selectedItem.spec}</span>}
              </p>
            )}
            {selectedItem?.description && (
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{selectedItem.description}</p>
              </div>
            )}
            {/* ダイアログ内数量選択 */}
            {selectedItem && (
              <div className="flex items-center justify-center gap-3 py-2">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-10 w-10"
                  onClick={() => updateQuantity(selectedItem, -1)}
                >
                  <Minus className="h-5 w-5" />
                </Button>
                <span className="w-12 text-center font-bold text-xl">
                  {getQuantity(selectedItem)}
                </span>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-10 w-10"
                  onClick={() => updateQuantity(selectedItem, 1)}
                >
                  <Plus className="h-5 w-5" />
                </Button>
              </div>
            )}
            <Button
              className={`w-full ${selectedItem?.is_sale ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}
              onClick={() => {
                handleAddToCart(selectedItem);
                setDetailDialogOpen(false);
              }}
            >
              <ShoppingCart className="h-4 w-4 mr-2" />
              カートに追加 ({selectedItem && getQuantity(selectedItem)}個)
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
